/**
 * Allergen badge with SVG icon for KDS display.
 * Renders allergen key as a colored badge with icon + label.
 */
import { AlertTriangle } from 'lucide-react';
import { allergenLabel } from '../utils/allergens';

interface Props {
  allergenKey: string;
  t?: (key: string) => string;
  /** "guest" = red, "product" = amber, "collision" = red pulsing danger */
  variant?: 'guest' | 'product' | 'collision';
}

export function AllergenBadge({ allergenKey, t, variant = 'guest' }: Props) {
  const key = allergenKey.startsWith('other:') ? 'other' : allergenKey;
  const path = ALLERGEN_PATHS[key];
  const label = t ? allergenLabel(allergenKey, t) : allergenKey;

  const colors = variant === 'collision'
    ? 'text-red-900 bg-red-200 border-red-400 ring-1 ring-red-300 animate-pulse font-black'
    : variant === 'guest'
      ? 'text-red-700 bg-red-100 border-red-200'
      : 'text-amber-700 bg-amber-50 border-amber-200';

  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-xs font-bold whitespace-nowrap ${colors}`}>
      {variant === 'collision' && <AlertTriangle className="h-3 w-3 shrink-0" aria-hidden="true" />}
      {path && (
        <svg width={14} height={14} viewBox="0 0 24 24" fill="none" className="shrink-0">
          <circle cx="12" cy="12" r="11" fill={path.bg} />
          <path d={path.d} fill={path.fg} fillRule="evenodd" clipRule="evenodd" />
        </svg>
      )}
      {label}
    </span>
  );
}

interface PathDef { d: string; bg: string; fg: string; }

const ALLERGEN_PATHS: Record<string, PathDef> = {
  celery:      { d: 'M12 5c-2 2-4 5-4 7s1.5 4 4 4 4-2 4-4-2-5-4-7zm0 2c1 1.5 2 3.5 2 5s-.8 2-2 2-2-.8-2-2 1-3.5 2-5z', bg: '#D1FAE5', fg: '#059669' },
  gluten:      { d: 'M12 4v12M10 7l2-1 2 1M10 10l2-1 2 1M10 13l2-1 2 1M10 16l2 2 2-2', bg: '#FEF3C7', fg: '#B45309' },
  crustaceans: { d: 'M8 8c0-2 2-3 4-3s4 1 4 3c0 1-.5 2-1.5 3L12 14l-2.5-3C8.5 10 8 9 8 8zm2 0c0-.5.8-1 2-1s2 .5 2 1-.8 1-2 1-2-.5-2-1zM10 15l2 3 2-3', bg: '#FFE4E6', fg: '#DC2626' },
  eggs:        { d: 'M12 5c-2.5 0-4.5 3-4.5 6.5S9.5 18 12 18s4.5-3 4.5-6.5S14.5 5 12 5zm0 2c1.5 0 2.5 2 2.5 4.5S13.5 16 12 16s-2.5-2-2.5-4.5S10.5 7 12 7z', bg: '#FEF9C3', fg: '#CA8A04' },
  fish:        { d: 'M6 12c0-2 2.5-4 6-4s6 2 6 4-2.5 4-6 4-6-2-6-4zm4 0a1 1 0 102 0 1 1 0 00-2 0zM4 12l2-2v4l-2-2z', bg: '#DBEAFE', fg: '#2563EB' },
  lupin:       { d: 'M12 5l1.5 3 3-.5-2 2.5 2 2.5-3-.5L12 15l-1.5-3-3 .5 2-2.5-2-2.5 3 .5L12 5z', bg: '#EDE9FE', fg: '#7C3AED' },
  milk:        { d: 'M12 4l4 7c0 3-1.8 5-4 5s-4-2-4-5l4-7zm0 3l-2 4c0 1.5.8 3 2 3s2-1.5 2-3l-2-4z', bg: '#F0F9FF', fg: '#0284C7' },
  molluscs:    { d: 'M12 6c3.3 0 6 2.7 6 6s-2.7 6-6 6-6-2.7-6-6 2.7-6 6-6zm0 2c-2.2 0-4 1.8-4 4s1.8 4 4 4 4-1.8 4-4-1.8-4-4-4zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z', bg: '#E0E7FF', fg: '#4338CA' },
  mustard:     { d: 'M12 6a6 6 0 100 12 6 6 0 000-12zm0 2a4 4 0 110 8 4 4 0 010-8zm0 1.5a2.5 2.5 0 100 5 2.5 2.5 0 000-5z', bg: '#FEF9C3', fg: '#A16207' },
  nuts:        { d: 'M9 8h6c.5 0 1 .5 1 1H8c0-.5.5-1 1-1zm-1 2c0 3 2 6 4 7 2-1 4-4 4-7H8z', bg: '#FED7AA', fg: '#92400E' },
  peanuts:     { d: 'M12 5c-1.7 0-3 1.3-3 3 0 .8.3 1.6.8 2.2-.5.6-.8 1.4-.8 2.3 0 2 1.5 3.5 3 3.5s3-1.5 3-3.5c0-.9-.3-1.7-.8-2.3.5-.6.8-1.4.8-2.2 0-1.7-1.3-3-3-3zm0 2c.6 0 1 .4 1 1s-.4 1-1 1-1-.4-1-1 .4-1 1-1zm0 4c.8 0 1.5.7 1.5 1.5S12.8 14 12 14s-1.5-.7-1.5-1.5S11.2 11 12 11z', bg: '#FECACA', fg: '#B91C1C' },
  sesame:      { d: 'M9 8a1.5 1.5 0 110 3 1.5 1.5 0 010-3zm6 0a1.5 1.5 0 110 3 1.5 1.5 0 010-3zm-3 3a1.5 1.5 0 110 3 1.5 1.5 0 010-3zm-3 3a1.5 1.5 0 110 3 1.5 1.5 0 010-3zm6 0a1.5 1.5 0 110 3 1.5 1.5 0 010-3z', bg: '#F5F5F4', fg: '#78716C' },
  soybeans:    { d: 'M12 5c-3 0-5 3-5 6 0 2 1 4 3 5 .5-1 1.5-2 2-2s1.5 1 2 2c2-1 3-3 3-5 0-3-2-6-5-6zm-1.5 4a1.5 1.5 0 110 3 1.5 1.5 0 010-3zm3 0a1.5 1.5 0 110 3 1.5 1.5 0 010-3z', bg: '#D9F99D', fg: '#4D7C0F' },
  sulphites:   { d: 'M12 7a3 3 0 100 6 3 3 0 000-6zm-4 5a2 2 0 100 4 2 2 0 000-4zm8 0a2 2 0 100 4 2 2 0 000-4z', bg: '#FDE68A', fg: '#92400E' },
  other:       { d: 'M15.5 5.5l3 3L9 18H6v-3l9.5-9.5zM14 8l2 2', bg: '#E5E7EB', fg: '#6B7280' },
};
